import { ErrorHandler, Injectable, Provider } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import posthog from 'posthog-js';

@Injectable({ providedIn: 'root' })
class PostHogErrorHandler implements ErrorHandler {
  public constructor() {}
  public handleError(error: unknown): void {
    const unwrapped = tryToUnwrapZonejsError(error);

    // Expected control-flow errors (e.g. an auth redirect to /login) are not
    // faults — skip reporting them to avoid flooding error tracking with noise.
    if (isExpectedError(unwrapped)) {
      return;
    }

    if (unwrapped instanceof HttpErrorResponse) {
      runOutsideAngular(() => captureHttpError(unwrapped));
      return;
    }

    if (isChunkLoadError(unwrapped)) {
      recoverFromChunkLoadError(unwrapped);
      return;
    }

    const extractedError = this._extractError(unwrapped) || 'Unknown error';
    runOutsideAngular(() => posthog.captureException(extractedError));
  }
  protected _extractError(error: unknown): unknown {
    if (typeof error === 'string' || isErrorOrErrorLikeObject(error)) {
      return error;
    }
    return null;
  }
}

const EXPECTED_ERROR_FLAG = '__gpcExpectedError';

/**
 * Build an Error that represents expected control flow (e.g. aborting an HTTP
 * request because the user is logged out and is being redirected to /login).
 * These are still thrown so subscribers stop their flows, but the global
 * PostHog error handler skips reporting them.
 */
export function expectedError(message: string): Error {
  const error = new Error(message);
  (error as unknown as Record<string, unknown>)[EXPECTED_ERROR_FLAG] = true;
  return error;
}

function isExpectedError(error: unknown): boolean {
  return (
    !!error &&
    typeof error === 'object' &&
    (error as Record<string, unknown>)[EXPECTED_ERROR_FLAG] === true
  );
}

function tryToUnwrapZonejsError(error: unknown): unknown | Error {
  return error && (error as { ngOriginalError: Error }).ngOriginalError
    ? (error as { ngOriginalError: Error }).ngOriginalError
    : error;
}

function captureHttpError(error: HttpErrorResponse): void {
  const routePattern = extractRoutePattern(error.url);
  const httpError = new Error(`${routePattern} — ${error.status} ${error.statusText}`);
  httpError.name = `HTTP ${error.status}`;
  posthog.captureException(httpError, {
    $exception_fingerprint: [`HTTP ${error.status}`, routePattern],
  });
}

function extractRoutePattern(url: string | null): string {
  if (!url) return '/unknown';
  try {
    const parsed = new URL(url);
    return parsed.pathname
      .replace(/\/[0-9A-Z]{26}\b/g, '/:id')
      .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '/:id')
      .replace(/\/\d+\b/g, '/:id');
  } catch {
    return url;
  }
}

/**
 * Every browser words a failed `import()` differently, so match all of them —
 * Chrome/Edge, Firefox and Safari each have their own phrasing.
 */
const CHUNK_LOAD_ERROR_MESSAGES = [
  'Failed to fetch dynamically imported module', // Chrome / Edge
  'error loading dynamically imported module',   // Firefox
  'Importing a module script failed',            // Safari
  'Failed to load module script',                // Chrome, wrong MIME type
  'Loading chunk',
  'ChunkLoadError',
];

function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return CHUNK_LOAD_ERROR_MESSAGES.some(candidate => message.includes(candidate));
}

const CHUNK_RELOAD_KEY = 'gpcChunkReloadAt';
const CHUNK_RELOAD_WINDOW_MS = 30_000;

/**
 * A chunk usually fails to load because the network dropped mid-navigation, so
 * a reload fixes it. When it does not (offline, blocked by a proxy), reloading
 * again would loop forever — so only reload once per window, then report.
 */
function recoverFromChunkLoadError(error: unknown): void {
  const lastReload = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0);
  const now = Date.now();

  if (lastReload && now - lastReload < CHUNK_RELOAD_WINDOW_MS) {
    const chunkError = new Error(
      error instanceof Error ? error.message : String(error),
    );
    chunkError.name = 'ChunkLoadError';
    runOutsideAngular(() => posthog.captureException(chunkError, {
      $exception_fingerprint: ['ChunkLoadError', 'reload-did-not-recover'],
      navigator_online: navigator.onLine,
    }));
    return;
  }

  sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
  window.location.reload();
}

function isErrorOrErrorLikeObject(value: unknown): value is Error {
  if (value instanceof Error) {
    return true;
  }
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  return 'name' in value && 'message' in value && 'stack' in value;
}

declare const Zone: any;
// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
const isNgZoneEnabled = typeof Zone !== 'undefined' && Zone.root?.run;
export function runOutsideAngular<T>(callback: () => T): T {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  return isNgZoneEnabled ? Zone.root.run(callback) : callback();
}

export function providePostHogErrorHandler(): Provider {
  return {
    provide: ErrorHandler,
    useValue: new PostHogErrorHandler(),
  };
}
